/**
 * Plan-limit refusals (`402 plan_limit`), as the client reads them.
 *
 * The limit blocks hold NO state of their own and call NO billing endpoint —
 * `GET /api/admin/billing` is superadmin-only, and an admin hitting a ceiling
 * deserves the same honest message. Everything a block renders comes from the
 * 402 body held by whichever mutation was refused.
 *
 * Types are deliberately local rather than imported from `shared/types.ts` —
 * that file is being extended by the billing work in parallel, and this module
 * must not collide with it. Consolidate later.
 */
import { ApiRequestError, apiFetch } from './client';

export type PlanLimitKey =
  | 'forms' | 'submissionsPerMonth' | 'users' | 'storageBytes'
  | 'fieldsPerForm' | 'aiGenerationsPerMonth' | 'aiAssistantMessagesPerMonth' | 'sso' | 'api' | 'customBranding' | 'teamAccess'
  | 'hideAdvertisement' | 'smartPdf' | 'aiBuilder' | 'webhooks';

/** The one refusal shape every enforced ceiling returns. */
export interface PlanLimitBody {
  error: 'plan_limit';
  limit: PlanLimitKey;
  used: number;
  allowed: number;
  /** key of the plan whose ceiling was hit, e.g. 'free' */
  plan: string;
  /** key of the cheapest plan that lifts it, or null from the top plan */
  upgradeTo: string | null;
}

/** The 402 body carried by a failed mutation, or null for any other failure. */
export function planLimitOf(err: unknown): PlanLimitBody | null {
  if (!(err instanceof ApiRequestError) || err.status !== 402) return null;
  const b = err.body as PlanLimitBody | undefined;
  return b && b.error === 'plan_limit' && b.limit ? b : null;
}

/**
 * Display names for the seeded plan keys, with a title-case fallback so a plan
 * somebody adds later reads as a name rather than a database key. A local copy
 * on purpose (the platform pages keep their own in `planDisplay.ts`) — the
 * admin and platform page trees do not import across each other.
 */
const PLAN_NAMES: Record<string, string> = {
  free: 'Free',
  team: 'Team',
  enterprise_limited: 'Enterprise Limited',
  enterprise_unlimited: 'Enterprise Unlimited',
};

export function planDisplayName(key: string | null | undefined): string {
  if (!key) return '';
  return PLAN_NAMES[key]
    || key.split('_').map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(' ');
}

/** The noun the refusal sentence counts — "all 5 forms", "all 10 user seats". */
const LIMIT_NOUNS: Record<PlanLimitKey, [singular: string, plural: string]> = {
  forms: ['form', 'forms'],
  submissionsPerMonth: ['submission this month', 'submissions this month'],
  users: ['user seat', 'user seats'],
  storageBytes: ['storage', 'storage'],
  fieldsPerForm: ['element on this form', 'elements on this form'],
  sso: ['SSO', 'SSO'],
  api: ['API access', 'API access'],
  customBranding: ['custom branding', 'custom branding'],
  // 048: folders, grants and groups are one feature and refuse as one, so the
  // sentence names the thing somebody was trying to do, not the key.
  teamAccess: ['folders and sharing', 'folders and sharing'],
  hideAdvertisement: ['badge removal', 'badge removal'],
  smartPdf: ['Smart PDF', 'Smart PDF'],
  aiGenerationsPerMonth: ['AI-generated form this month', 'AI-generated forms this month'],
  aiAssistantMessagesPerMonth: ['AI assistant question this month', 'AI assistant questions this month'],
  aiBuilder: ['AI form building', 'AI form building'],
  webhooks: ['account webhooks', 'account webhooks'],
};

export const limitNoun = (limit: PlanLimitKey, n: number): string =>
  (LIMIT_NOUNS[limit] || [limit, limit])[n === 1 ? 0 : 1];

/**
 * Switch a form's collecting off or on. Disabling always succeeds and frees
 * the form's slot immediately; re-enabling past the form ceiling comes back
 * as a `402 plan_limit`.
 */
export const setFormDisabledApi = (id: number | string, disabled: boolean) =>
  apiFetch<{ success: boolean; form: { id: number; disabled_at: string | null } | null }>(
    `/admin/forms/${id}/disabled`,
    { method: 'PATCH', body: JSON.stringify({ disabled }) },
  );
